import { useEffect } from 'react';

import Maintenance from './Maintenance';
import useIsGameInMaintenance from './useIsGameInMaintenance';

import { useExitGame } from '@/utilities/helpers/useExitGame';
import { usePopup } from '@/features/Popup/usePopup';
import { stopVitalsOnError } from '@/utilities/logger/datadog/vitals';

/**
 * Renders the maintenance page instead of the game when the game is disabled
 * (Prismic `status_overall`), the region is in maintenance, the game is hidden
 * for the user, or the launch game uid is invalid (no provider). Otherwise, it
 * renders the game tree. See {@link useIsGameInMaintenance} for the decision.
 */
const MaintenanceWrapper = ({ children }: { children: React.ReactNode }) => {
  const isMaintenance = useIsGameInMaintenance();
  const exitWithTracking = useExitGame();
  const { hidePopup } = usePopup();

  // Clear any launch-error popup that may have opened before maintenance was
  // resolved (e.g. an invalid gameUid) so the maintenance page shows cleanly,
  // and stop any launch vitals still hanging since the game will never launch.
  useEffect(() => {
    if (isMaintenance) {
      stopVitalsOnError({ errorType: 'maintenance' });
      hidePopup();
    }
  }, [isMaintenance, hidePopup]);

  return isMaintenance ? <Maintenance exitGame={exitWithTracking} /> : children;
};

export default MaintenanceWrapper;
